import { ACCESS_TYPE_VALUE } from '@typings/common/accessType';
import { CLOSET_TAB_TYPE_VALUE } from './closetType';
import { IthemeColorSet } from '@typings/setting/themeColor.interface';
import { statusType } from '@typings/status/statusItem.interface';
import type { PhotocardTabKey, PhotocardTabValue } from '@typings/photocard/photocardTabs';
import type { ActionBarPosition, ClosetMenuType, ClosetMenuPosition } from '@typings/photocard/photocardLayout.types';

export interface IselectPhotocardList extends IphotocardCharList {
  thumbnail: string;
  value: string;
}

export interface IphotocardCharList {
  key: string;
  path: string;
  thumbnail?: string;
  value?: string;
}

export interface IAddPhotoCard {
  photoCardId?: string;
  order: number;
  title: string;
  authorUid?: string;
  subTitle?: string;
  description?: string;
  thumbnailFile?: File;
}

export interface IPhotoCard {
  id?: string;
  createdAt?: Date;
  editedAt?: Date;
  order: number;
  authorUid: string;
  title: string;
  photoCardId?: string;
  subTitle?: string;
  description?: string;
  thumbnail?: string;
  hoverThumbnail?: string;
  bgmURL?: string;
  accessType?: ACCESS_TYPE_VALUE;
  password?: string;
  passwordHint?: string;
  soundURL?: {
    url: string;
    name: string;
  };
  linkStickerboardId?: string;
  isShowStickerboardLink?: boolean;
  owner?: string;

  camp?: string;
  isShow?: boolean; // npc로 등록된 경우 숨김
  path?: string; // collectionPath
  //status
  [statusType.ATTACK]?: number;
  [statusType.DEFENSE]?: number;
  [statusType.CRITICAL]?: number;
  [statusType.HP]?: number;
  [statusType.MP]?: number;
  [statusType.SPEED]?: number;
  [statusType.STATUS1]?: number;
  [statusType.STATUS2]?: number;
  [statusType.STATUS3]?: number;
  [statusType.STATUS4]?: number;
  [statusType.STATUS5]?: number;

  // 현재 HP/MP (최대값은 위의 hp, mp 필드)
  currentHp?: number;
  currentMp?: number;
  // 캐릭터 개별 총 스탯 합산 포함 여부 오버라이드
  // key: stat id 또는 stat key, value: 포함 여부
  totalStatIncludeMap?: Record<string, boolean>;

  // 캐릭터 스탠딩 & 대사 설정
  standingConfig?: IStandingCharacter | null;

  // 🚀 파견 시스템 관련 필드
  // 파견 트래킹용
  dispatchAreaId?: string; // 파견 중인 지역 ID
  dispatchOptionId?: string; // 선택한 파견 옵션 ID (서브컬렉션 키)
  isDispatched?: boolean; // 파견 상태
  dispatchStartedAt?: Date | string; // 파견 시작 시간
  dispatchDuration?: number; // 파견 소요 시간 (분 단위)
  // 파견 히스토리
  dispatchHistory?: IDispatchHistory[];

  // 폼 마이그레이션 출처 — 설정 시 포토카드 삭제 시 원본 이미지를 Storage에서 삭제하지 않음
  sourceFormId?: string;
}

// 🚀 파견 히스토리 인터페이스
export interface IDispatchHistory {
  id?: string;
  areaId: string; // 파견 지역 ID
  areaName?: string; // 파견 지역 이름
  startedAt: Date | string; // 파견 시작 시간
  endedAt?: Date | string; // 파견 종료 시간
  // 파견으로 얻은 보상
  rewards?: {
    investigationText?: {
      id: string;
      title: string;
      content: string;
    };
    itemId?: string;
    itemTitle?: string;
    itemImgURL?: string;
    // 복수 아이템 지급 시 전체 목록
    items?: {
      id: string;
      title: string;
      imgURL?: string;
      count: number;
    }[];
  };
}

export interface IStandingCharacter {
  baseImgURL?: string;
  expressions?: { name: string; imgURL: string }[];
  scripts?: { text: string; type: 'casual' | 'onClick' | 'byExpression'; expressionName?: string }[];
}

export interface IClosetStandingConfig {
  enabled: boolean;
  imageFit: 'fit' | 'original';
  dialoguePosition?: 'top' | 'bottom';
  expressions?: { name: string; imgURL: string; _file?: File | null }[];
  scripts?: { text: string; type: 'default' | 'onClick'; expressionName?: string }[];
}

// 포토카드별 인벤토리 설정 (관리자 설정 — ICloset.inventoryConfig 에 저장)
export interface IPhotoCardInventoryConfig {
  viewType: 'list' | 'grid';  // 뷰 타입 (기본: 'list')
  gridColumns: number;         // 그리드 열 수 (1~20, 기본: 10)
  gridRows: number;            // 관리자 설정 기본 행 수 (1~100, 기본: 8)
  expandedRows?: number;       // 확장권으로 추가된 행 수 (기본: 0)
  // 유효 총 행 수 = gridRows + (expandedRows ?? 0) — 최대 200칸
}

export const DEFAULT_INVENTORY_CONFIG: IPhotoCardInventoryConfig = {
  viewType: 'list',
  gridColumns: 10,
  gridRows: 8,
  expandedRows: 0,
};

/** 최대 슬롯 수 상한 */
export const MAX_INVENTORY_SLOTS = 200;

export interface ICloset {
  id?: string;
  authorUid: string;
  photoCardId?: string;
  closetId?: string;
  order: number;
  closetName: string;
  createdAt: Date;
  editedAt: Date;
  title: string;
  tag: string;
  subTitle?: string;
  description?: string;
  imgURL?: string;
  note?: string;
  secretNote?: string; // 비밀설정 탭 내용 (관리자/오너만 열람 가능)
  thumbnail?: string;
  customTabList?: IPhotocardTab[] | Partial<Record<PhotocardTabKey, PhotocardTabValue>>;
  //dday
  showDday?: boolean;
  targetDate?: any;
  ddayText?: string;
  //잔상
  showAfterImg?: boolean;
  afterImgOffsetX?: number; // 잔상 이미지 X축 위치(px)
  afterImgOffsetY?: number; // 잔상 이미지 Y축 위치(px)
  //진영 표시
  showCamp?: boolean;
  themeSet?: IthemeColorSet;
  // 옷장별 개별 BGM (테마 BGM보다 우선 적용)
  bgmURL?: string;
  // 레이아웃 오버라이드 (미설정 시 전역 설정 따름)
  actionButtonPosition?: ActionBarPosition;
  // 옷장별 스탠딩 대사 설정
  closetStandingConfig?: IClosetStandingConfig;
  // 인벤토리 표시 설정 (관리자 설정)
  inventoryConfig?: IPhotoCardInventoryConfig;
}

export interface IClosetTab {
  title: string;
  order: number;
  id: CLOSET_TAB_TYPE_VALUE;
  isHidden: boolean;
}

export interface IAlbum {
  id?: CLOSET_TAB_TYPE_VALUE;
  title?: string;
  description?: string;
  imgURL?: string;
  createdAt?: Date;
  editedAt?: Date;
  date?: Date | null;
  authorUid?: string;
}

export interface IAddAlbum {
  title: string;
  description?: string;
  selectedFile: File | null;
  createdAt?: Date;
  editedAt?: Date;
  prevImgURL?: string;
}
export interface IPhotocardTab {
  key: string;
  value: string;
  imgURL?: string;
  isActive?: boolean;
}

export interface IPhotocardAutoSet {
  // photocard only
  defaultContents?: string;
  customTabList?: IPhotocardTab[] | Partial<Record<PhotocardTabKey, PhotocardTabValue>>;
  showDefaultCustomTab?: boolean;
  customWidth?: string;
  isShowSeries?: boolean;
  themeSet?: IthemeColorSet | null;
  useImage?: boolean;
  useImgShadow?: boolean;
  imgShadowColor?: string;
  // 레이아웃 설정
  actionButtonPosition?: ActionBarPosition;
  closetMenuType?: ClosetMenuType;
  closetMenuPosition?: ClosetMenuPosition;
  standingImageFit?: 'fit' | 'original';
  // BGM (2순위: 포토카드 전역 BGM)
  bgmURL?: string;
  // 인벤토리 일괄 설정 (viewType, gridColumns, gridRows — expandedRows는 개별 closet 문서에서 관리)
  inventoryConfig?: IPhotoCardInventoryConfig;
}

// 📍 프로필 템플릿 인터페이스
export interface IProfileTemplate {
  id?: string;
  title: string;
  contents: string;
  order: number;
  isDefault?: boolean; // 기본 템플릿 여부
  createdAt?: Date;
  editedAt?: Date;
}
